import React, { useState, useEffect, useCallback, useContext } from "react";
import PropTypes from "prop-types";
import { Editor } from "react-draft-wysiwyg";
import { debounce } from "lodash";
import draftToHtml from "draftjs-to-html";
import {
  EditorState,
  ContentState,
  convertFromHTML,
  convertToRaw,
} from "draft-js";
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";

import {
  Box,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  IconButton,
  Stepper,
  Step,
  StepLabel,
  Alert,
  RadioGroup,
  FormControl,
  FormControlLabel,
  Radio,
} from "@mui/material";
import AlertsTab from "./TabsSections/AlertsTab";
import NotesTab from "./TabsSections/NotesTab";
import api from "../../api/axios";
import { CustomButtonPlusIcon } from "../../Components/Common/Design/CustomButtonPlusIcon";
import { Delete, Mail } from "lucide-react";
import { DocsContext } from "../../Components/Context/Context";
import {
  Add,
  Close,
  DeleteOutline,
  NoteAdd,
  Warning,
} from "@mui/icons-material";
import LoadingPage from "../../Components/Common/Loading/LoadingPage";
import { useNavigate } from "react-router-dom";
import MessagesTab from "./TabsSections/MessagesTab";
import { COLORS } from "./muiStyles/muiStyles";

import dayjs from "dayjs";

const TabsSection = ({ caseId, lawyerData, dataCase, onFinish }) => {
  const Swal = require("sweetalert2");
  const navigate = useNavigate();

  // ===============================
  // STATE VARIABLES
  // ===============================
  const [dataCaseNotesAlertsMessages, setDataCaseNotesAlertsMessages] =
    useState([]);

  const DEFAULT_TAB = 1;
  const [tabValue, setTabValue] = useState(DEFAULT_TAB);

  const [addDataTab, setAddDataTab] = useState(false);
  const [editDataTab, setEditDataTab] = useState(false);

  const SAFE_TABS = [0, 1, 2];

  const safeSetTabValue = (value) => {
    const normalized = Number(value);
    if (Number.isInteger(normalized) && SAFE_TABS.includes(normalized)) {
      setTabValue(normalized);
    } else {
      setTabValue(DEFAULT_TAB);
    }
  };

  ////

  const [editorState, setEditorState] = useState(EditorState.createEmpty());
  const [noteId, setNoteId] = useState(null);
  const [messageId, setMessageId] = useState(null);
  const [alertId, setAlertId] = useState(null);

  const [tabsDataChanged, setTabsDataChanged] = useState(false);

  // Modal
  const [openModal, setOpenModal] = useState(false);

  // DELETE MODAL
  const [loading, setLoading] = useState(false);

  const { docsInfo } = useContext(DocsContext);
  const [activeStepDelete, setActiveStepDelete] = useState(0);
  const [openModalDelete, setOpenModalDelete] = useState(false);
  const [errorDoc, setErrorDocs] = useState(false);
  const [docsDecision, setDocsDecision] = useState(0);

  const stepsChangeStatus = ["Inicio", "Finalizar"];
  const stepsDelete = ["Inicio", "Documentación", "Eliminar Expediente"];

  // Estados de selección
  const [selectionStyleAlert, setSelectionStyleAlert] = useState(null);
  const [selectionStyleNote, setSelectionStyleNote] = useState(null);
  const [selectionStyleMessage, setSelectionStyleMessage] = useState(null);

  // Control para detectar cambios
  const [unsavedChanges, setUnsavedChanges] = useState(false);

  const [dateConfig, setDateConfig] = React.useState(dayjs());

  // DELETE MODAL

  const a11yProps = (index) => ({
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  });

  // ===============================
  // FETCH NOTES/ALERTS
  // ===============================
  const getDataAlertsNotesMessages = async () => {
    const url = `${process.env.REACT_APP_API_URL}/cases/getalertsandnotes`;
    const formData = new FormData();
    formData.append("id", caseId);

    try {
      const notesAlerts = await api.post(url, formData);
      const { alerts, notes, messages } = notesAlerts.data.notes_alerts;
      setDataCaseNotesAlertsMessages({ alerts, notes, messages });
    } catch (error) {
      console.error("Error fetching notes/alerts data:", error);
    }
  };

  useEffect(() => {
    getDataAlertsNotesMessages();
  }, [tabsDataChanged]);

  // ===============================
  // EDITOR HANDLERS
  // ===============================
  const clearEditor = () => {
    setEditorState(EditorState.createEmpty());
    setUnsavedChanges(false);
  };

  const onEditorChange = useCallback(
    debounce((newState) => {
      setEditorState(newState);
      setUnsavedChanges(true);
    }, 50),
    [],
  );

  const handleEditEditorDataTabs = (message) => {
    if (!message) return;
    const blocksFromHTML = convertFromHTML(message);
    const contentState = ContentState.createFromBlockArray(
      blocksFromHTML.contentBlocks,
      blocksFromHTML.entityMap,
    );
    setEditorState(EditorState.createWithContent(contentState));
    setUnsavedChanges(false);
  };

  const handleCancelDataTabs = () => {
    if (unsavedChanges) {
      Swal.fire({
        title: "¿Descartar cambios?",
        text: "Hay cambios sin guardar. ¿Seguro que deseas cancelar?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#1ab394",
        cancelButtonColor: "#d33",
        reverseButtons: true,
        confirmButtonText: "Sí, descartar",
        cancelButtonText: "Volver",
      }).then((result) => {
        if (result.isConfirmed) {
          clearEditor();
          setAddDataTab(false);
          setEditDataTab(false);
          setNoteId(null);
          setAlertId(null);
          setMessageId(null);
          // setOpenModal(false);
        }
      });
    } else {
      clearEditor();
      setAddDataTab(false);
      setEditDataTab(false);
      setNoteId(null);
      setAlertId(null);
      setMessageId(null);
      // setOpenModal(false);
    }
  };

  // ===============================
  // MODAL HANDLERS
  // ===============================
  const handleOpenModal = (
    tabIndex = 1,
    isEdit = false,
    id = null,
    message = "",
  ) => {
    safeSetTabValue(tabIndex);

    // setTabValue(tabIndex);
    setAddDataTab(true);
    setEditDataTab(isEdit);
    if (tabIndex === 0) setAlertId(id);
    if (tabIndex === 1) setNoteId(id);
    if (tabIndex === 2) setMessageId(id);
    if (isEdit) handleEditEditorDataTabs(message);
    else clearEditor();
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    clearEditor();
    // setAddDataTab(false);
    // setEditDataTab(false);
    setNoteId(null);
    setAlertId(null);
    setMessageId(null);

    setTabValue(DEFAULT_TAB); // 🔒 evita value=""
  };

  // ===============================
  // SUBMIT HANDLER
  // ===============================
  const handleSubmitDataTabs = (rute, dataType, date = "") => {
    const htmlContent = draftToHtml(
      convertToRaw(editorState.getCurrentContent()),
    );

    if (!htmlContent || htmlContent === "<p></p>") {
      Swal.fire("Error", "El contenido está vacío", "warning");
      return;
    }

    const formData = new FormData();

    console.log(date);
    // CREAR
    if (!editDataTab) {
      formData.append("message", htmlContent);
      formData.append("user_id", lawyerData.id);
      formData.append("case_id", dataCase.id);
      formData.append("date", date?.date);

      Swal.fire({
        title: `¿Desea guardar la ${dataType}?`,
        html: `<div>${htmlContent}</div>`,
        icon: "warning",
        showDenyButton: true,
        confirmButtonText: "Sí",
        denyButtonText: "No",
        confirmButtonColor: "#1ab394",
        cancelButtonColor: "#d33",
        reverseButtons: true,
        preConfirm: async () => {
          // Loader mientras guarda
          Swal.fire({
            title: "Guardando...",
            text: "Por favor espera",
            allowOutsideClick: false,
            allowEscapeKey: false,
            didOpen: () => Swal.showLoading(),
          });

          const url = `${process.env.REACT_APP_API_URL}/${rute}/store`;

          try {
            await api.post(url, formData);
            Swal.close();

            // ÉXITO
            await Swal.fire({
              icon: "success",
              title: "Guardado exitosamente",
              timer: 1500,
              showConfirmButton: false,
            });

            setTabsDataChanged(!tabsDataChanged);
          } catch (error) {
            console.error(error);
            Swal.fire("Error", "No se pudo guardar", "error");
          }
        },
      }).then(() => {
        clearEditor();
      });
    }

    // EDITAR
    else {
      const id = dataType === "alerts" ? alertId : noteId ? noteId : messageId;
      formData.append("id", id);
      formData.append("message", htmlContent);

      Swal.fire({
        title: `¿Desea editar la ${dataType}?`,
        html: `<div>${htmlContent}</div>`,
        icon: "warning",
        showDenyButton: true,
        confirmButtonText: "Sí",
        denyButtonText: "No",
        confirmButtonColor: "#1ab394",
        cancelButtonColor: "#d33",
        reverseButtons: true,
        preConfirm: async () => {
          Swal.fire({
            title: "Guardando cambios...",
            text: "Por favor espera",
            allowOutsideClick: false,
            allowEscapeKey: false,
            didOpen: () => Swal.showLoading(),
          });

          const url = `${process.env.REACT_APP_API_URL}/${dataType}/update`;

          try {
            await api.post(url, formData);
            Swal.close();

            // ÉXITO
            await Swal.fire({
              icon: "success",
              title: "Actualizado exitosamente",
              timer: 1500,
              showConfirmButton: false,
            });

            setTabsDataChanged(!tabsDataChanged);
          } catch (error) {
            console.error(error);
            Swal.fire("Error", "No se pudo actualizar", "error");
          }
        },
      }).then(() => {
        clearEditor();
      });
    }
  };

  // ===============================
  // DELETE HANDLER
  // ===============================
  const handleDeleteDataTabs = async (id, message, dataType, title) => {
    const result = await Swal.fire({
      title: `¿Eliminar ${title}?`,
      html: `<p style="margin:0">${message}</p>`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sí, eliminar",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#d33",
      cancelButtonColor: "#6c757d",
      reverseButtons: true,
      focusCancel: true,
    });

    if (!result.isConfirmed) return;

    Swal.fire({
      title: "Eliminando...",
      text: "Por favor espere",
      allowOutsideClick: false,
      allowEscapeKey: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });

    try {
      const url = `${process.env.REACT_APP_API_URL}/${dataType}/destroy`;
      const formData = new FormData();
      formData.append("id", id);

      await api.post(url, formData);

      setTabsDataChanged((prev) => !prev);

      Swal.fire({
        title: "Eliminado",
        text: `${title} eliminad@ correctamente.`,
        icon: "success",
        confirmButtonColor: "#1ab394",
      });
    } catch (error) {
      console.error(error);

      Swal.fire({
        title: "Error",
        text: "No se pudo eliminar el registro. Intente nuevamente.",
        icon: "error",
        confirmButtonColor: "#d33",
      });
    }
  };

  // ======================================================
  // FUNCION: Eliminar caso
  // ======================================================
  const handleDeleteCase = async (flag = false) => {
    // Swal de confirmación
    const result = await Swal.fire({
      title: "¿Estás seguro?",
      text: "Esta acción eliminará el caso. No podrás deshacerla.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Sí, eliminar",
      cancelButtonText: "Cancelar",
      reverseButtons: true,
    });

    // Si cancela, no hace nada
    if (!result.isConfirmed) {
      handleCloseModalDelete();
      handleBackDelete();

      return;
    }

    setLoading(true);
    const formData = new FormData();
    formData.append("case_id", Number(caseId));
    formData.append("keep_doc", flag);

    if (flag) {
      const url = `${process.env.REACT_APP_API_URL}/cases/delete`;
      try {
        const response = await api.post(url, formData);
        setLoading(false);

        // Swal.fire({
        //   title: "Eliminado",
        //   text: "El caso ha sido eliminado correctamente.",
        //   icon: "success",
        //   confirmButtonColor: "#1ab394",
        // });

        return response;
      } catch (error) {
        console.error(error);
        setLoading(false);

        Swal.fire({
          title: "Error",
          text: "Hubo un problema al eliminar el caso.",
          icon: "error",
          confirmButtonColor: "#d33",
        });
      }
    }
  };

  // ======================================================
  // FUNCION: Preparar documentos para eliminación múltiple
  // ======================================================
  const deleteMultipleDocuments = async () => {
    setLoading(true);
    const allDocs = [];

    Object.entries(docsInfo).forEach(([caseId, categories]) => {
      Object.entries(categories).forEach(([category, docsArray]) => {
        docsArray.forEach((doc) => {
          allDocs.push({ doc_id: doc.doc_id || null, path: doc.path });
        });
      });
    });

    console.log(allDocs);

    if (allDocs?.length > 0) {
      const formData = new FormData();
      formData.append("documents", JSON.stringify(allDocs));

      try {
        const { data } = await api.post(
          `${process.env.REACT_APP_API_URL}/s3/destroymultiple`,
          formData,
        );

        Swal.close();
        if (data.success) {
          setLoading(false);
          return data.success;
        } else if (data?.error?.errorInfo?.length > 0) setErrorDocs(true);
      } catch (error) {
        Swal.close();
        Swal.fire({
          title: "¡Error de conexión!",
          icon: "error",
          text: "No se pudo eliminar los archivos.",
          confirmButtonColor: "#1ab394",
          confirmButtonText: "Continuar",
        });
        console.error(error);
      }
    }
  };

  // ======================================================
  // FUNCIONES: Retroceder pasos
  // ======================================================
  const handleBackDelete = () => {
    setActiveStepDelete((prev) => Math.max(prev - 1, 0));
    setDocsDecision(0);
  };

  const handleNext = async (flag) => {
    setLoading(true);

    // ---------- CASO: Eliminar ----------
    if (flag === "eliminar") {
      console.log("Entra", activeStepDelete);

      // Avanzar paso
      setActiveStepDelete((prev) => Math.min(prev + 1, stepsDelete.length - 1));

      if (activeStepDelete === 0) {
        // Simular retraso antes de continuar
        setTimeout(() => {
          console.log("1S después");
          setLoading(false);
        }, 1000);
      } else if (activeStepDelete === 1) {
        console.log(typeof docsDecision, "docsDecision");

        if ([0, 1].includes(Number(docsDecision))) {
          console.log("Conservar doc");
          await handleDeleteCase(true);
        } else if (docsDecision == 2) {
          console.log("Eliminar doc");

          const docsToDelete = deleteMultipleDocuments();

          if (docsToDelete) {
            console.log("Docs preparados para eliminar:", docsToDelete);
            const responseCase = await handleDeleteCase(true);
            console.log("Case eliminado:", responseCase);
          } else {
            console.warn("No hay documentos para eliminar");
            setLoading(false);
          }
        }
      }
    }
  };

  const handleOpenModalDelete = (flag) => {
    if (flag == "eliminar") {
      setOpenModalDelete(true);
    }
  };

  const handleCloseModalDelete = () => {
    setOpenModalDelete(false);
    setDocsDecision(0);
    setActiveStepDelete(0);
    // setRevertCaseFlow(null);
  };

  const handleFinish = (flag) => {
    if (flag == "delete") navigate("/expedientes");

    handleCloseModalDelete();
    onFinish && onFinish();
  };

  const hasDocs = Object.keys(docsInfo || {}).length > 0;
  const needsDecision = activeStepDelete === 1 && hasDocs;
  const noDecisionSelected =
    docsDecision === null || docsDecision === undefined || docsDecision === "";
  // ===============================
  // RENDER
  // ===============================
  return (
    <Box
      sx={{
        p: "10px",
        borderRadius: "4px",
        width: "100%",
        //  display: "contents"
      }}>
      {/* ================= MODAL: Eliminar expediente ================= */}
      <Dialog
        open={openModalDelete}
        onClose={
          activeStepDelete === stepsDelete.length - 1
            ? null
            : handleCloseModalDelete
        }
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            minHeight: "550px", // altura personalizada
          },
        }}>
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            backgroundColor: "#d32f2f",
            color: "white",
            fontWeight: "700",
            mb: 3,
            p: 2,
          }}>
          Eliminar expediente
          <IconButton
            aria-label="cerrar"
            onClick={handleCloseModalDelete}
            disabled={activeStepDelete === stepsDelete.length - 1}
            sx={{ color: "white" }}>
            <Close />
          </IconButton>
        </DialogTitle>

        <DialogContent>
          <Box>
            <Stepper activeStep={activeStepDelete} alternativeLabel>
              {stepsDelete.map((label) => (
                <Step key={label}>
                  <StepLabel
                    slots={{
                      stepIcon: (props) => (
                        <div
                          style={{
                            backgroundColor: "#d32f2f",
                            color: "white",
                            borderRadius: "4px",
                            width: 28,
                            height: 28,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: "0.8rem",
                          }}>
                          {props.icon}
                        </div>
                      ),
                    }}>
                    {label}
                  </StepLabel>
                </Step>
              ))}
            </Stepper>

            <Box mt={5}>
              {activeStepDelete === 0 && (
                <div>
                  <Alert severity="warning">
                    Estás a punto de eliminar el Expediente completamente.
                  </Alert>
                  <Alert severity="info">
                    Clic en siguiente para continuar.
                  </Alert>
                </div>
              )}
              {activeStepDelete === 1 && (
                <div style={{ position: "relative", height: "170px" }}>
                  {loading ? (
                    <>
                      <p style={{ color: "#0c1e35", textAlign: "center" }}>
                        <em>Verificando datos vinculados al expediente...</em>
                      </p>
                      <LoadingPage
                        isLoading={loading}
                        heightProp={"100px"}
                        widthProp={"100px"}
                      />
                    </>
                  ) : Object.entries(docsInfo).length > 0 ? (
                    <div>
                      <Alert severity="warning">
                        <b
                          style={{
                            color: "rgb(102, 60, 0)",
                            marginRight: "10px",
                          }}>
                          ¡Cuidado!
                        </b>
                        <span>
                          Se ha encontrado documentación asociada al caso.
                        </span>
                      </Alert>
                      <Alert severity="warning">
                        Al clicar en ELIMINAR EXPEDIENTE, toda la información
                        asociada al expediente será eliminada.
                      </Alert>
                      <Alert severity="info">
                        ¿Desea conservar la documentación del caso? En caso de
                        no hacerlo, se perderá para siempre. Por favor
                        seleccione una opción:
                      </Alert>
                      <FormControl
                        sx={{
                          "& .MuiFormControlLabel-label": { fontSize: 14 },
                          mt: 1,
                        }}>
                        <RadioGroup
                          aria-labelledby="radioNewCaseStatus"
                          name="radio-buttons-group"
                          value={docsDecision} // <- controlado por estado
                          onChange={(event) =>
                            setDocsDecision(event.target.value)
                          }>
                          <FormControlLabel
                            value="1"
                            control={<Radio />}
                            label="Conservar documentación."
                          />
                          <FormControlLabel
                            value="2"
                            control={<Radio />}
                            label="Eliminar documentación."
                          />
                        </RadioGroup>
                      </FormControl>
                    </div>
                  ) : (
                    <>
                      <Alert severity="success">
                        No se ha encontrado documentación asociada al
                        expediente, puede proseguir.
                      </Alert>
                      <Alert severity="warning">
                        Al clicar en ELIMINAR EXPEDIENTE, toda la información
                        asociada al expediente será eliminada.
                      </Alert>
                    </>
                  )}
                </div>
              )}
              {activeStepDelete === 2 && (
                <div style={{ position: "relative", height: "170px" }}>
                  {loading ? (
                    <>
                      <p style={{ color: "#0c1e35", textAlign: "center" }}>
                        <em>Eliminando expediente...</em>
                      </p>
                      <LoadingPage
                        isLoading={loading}
                        heightProp={"100px"}
                        widthProp={"100px"}
                      />
                    </>
                  ) : errorDoc ? (
                    <Alert severity="error">
                      Ha ocurrido un error al procesar la solicitud.
                    </Alert>
                  ) : (
                    <Box>
                      <Alert severity="success">
                        Expediente eliminado correctamente, Click en finalizar
                        para terminar el proceso.
                      </Alert>
                    </Box>
                  )}
                </div>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ display: "block" }}>
          <Box mt={2} display="flex" justifyContent="space-between">
            <Button
              disabled={!loading && activeStepDelete === 2}
              onClick={
                activeStepDelete === 0
                  ? handleCloseModalDelete
                  : handleBackDelete
              }
              variant="outlined"
              color="error">
              {activeStepDelete === 0 ? "Cancelar" : "Atrás"}
            </Button>
            {activeStepDelete === stepsDelete.length - 1 ? (
              <Button
                variant="contained"
                onClick={() => handleFinish("delete")}
                disabled={loading}>
                Finalizar
              </Button>
            ) : (
              <Button
                variant="contained"
                onClick={() => handleNext("eliminar")}
                color="error"
                disabled={needsDecision && noDecisionSelected}>
                {activeStepDelete === 1 ? "Eliminar Expediente" : "Siguiente"}
              </Button>
            )}
          </Box>
        </DialogActions>
      </Dialog>

      {SAFE_TABS.includes(tabValue) && (
        <Tabs
          sx={{
            background: "white",
          }}
          value={typeof tabValue === "number" ? tabValue : DEFAULT_TAB}
          onChange={(e, newVal) => safeSetTabValue(newVal)}>
          <Tab label="Alertas" value={0} />
          <Tab label="Notas" value={1} />
          <Tab label="Mensajes" value={2} />
        </Tabs>
      )}
      {tabValue === 0 && (
        <AlertsTab
          handleDeleteDataTabs={handleDeleteDataTabs}
          addDataTab={addDataTab}
          setAddDataTab={setAddDataTab}
          editDataTab={editDataTab}
          setEditDataTab={setEditDataTab}
          Editor={Editor}
          editorState={editorState}
          onEditorChange={onEditorChange}
          handleCancelDataTabs={handleCancelDataTabs}
          handleSubmitDataTabs={handleSubmitDataTabs}
          dataCaseNotesAlertsMessages={dataCaseNotesAlertsMessages}
          setSelectionStyleAlert={setSelectionStyleAlert}
          selectionStyleAlert={selectionStyleAlert}
          dateConfig={dateConfig}
          setDateConfig={setDateConfig}
        />
      )}
      {tabValue === 1 && (
        <NotesTab
          addDataTab={addDataTab}
          editDataTab={editDataTab}
          setAddDataTab={setAddDataTab}
          setEditDataTab={setEditDataTab}
          Editor={Editor}
          editorState={editorState}
          onEditorChange={onEditorChange}
          dataCaseNotesAlertsMessages={dataCaseNotesAlertsMessages}
          setSelectionStyleNote={setSelectionStyleNote}
          selectionStyleNote={selectionStyleNote}
          handleDeleteDataTabs={handleDeleteDataTabs}
          handleCancelDataTabs={handleCancelDataTabs}
          handleSubmitDataTabs={handleSubmitDataTabs}
        />
      )}
      {tabValue === 2 && (
        <MessagesTab
          dataCase={dataCase}
          addDataTab={addDataTab}
          editDataTab={editDataTab}
          setAddDataTab={setAddDataTab}
          setEditDataTab={setEditDataTab}
          Editor={Editor}
          editorState={editorState}
          onEditorChange={onEditorChange}
          dataCaseNotesAlertsMessages={dataCaseNotesAlertsMessages}
          setSelectionStyleMessage={setSelectionStyleMessage}
          selectionStyleMessage={selectionStyleMessage}
          handleDeleteDataTabs={handleDeleteDataTabs}
          handleCancelDataTabs={handleCancelDataTabs}
          handleSubmitDataTabs={handleSubmitDataTabs}
        />
      )}
      {/* </DialogContent> */}
      {/* <DialogActions>
          <Button onClick={handleCloseModal} color="error">
            Cancelar
          </Button>
          <Button
            onClick={() =>
              handleSubmitDataTabs(tabValue === 0 ? "alerts" : "notes")
            }
            color="primary">
            Guardar
          </Button>
        </DialogActions> */}
      {/* </Dialog> */}
    </Box>
  );
};

export default TabsSection;
